<template>
  <div class="h-full flex flex-col">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-purple-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
        </svg>
        <h3 class="text-xl font-bold text-gray-800">Task Manager</h3>
      </div>
      <div class="text-sm text-gray-500">{{ tasks.length }} tasks</div>
    </div>

    <!-- Add new task form -->
    <div class="mb-4 p-4 bg-gray-50 rounded-lg">
      <div class="space-y-3">
        <!-- Task description -->
        <div>
          <input
            v-model="newTask.task"
            @keyup.enter="addTask"
            type="text"
            placeholder="Add a new task..."
            class="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <!-- Optional fields toggle -->
        <div class="flex items-center space-x-2">
          <button
            @click="showAdvancedFields = !showAdvancedFields"
            class="text-sm text-purple-600 hover:text-purple-800 flex items-center"
          >
            <svg class="w-4 h-4 mr-1" :class="{ 'rotate-90': showAdvancedFields }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
            Advanced Options
          </button>
        </div>

        <!-- Advanced fields -->
        <div v-if="showAdvancedFields" class="space-y-3 pt-2 border-t border-gray-200">
          <!-- Phone number -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Contact Phone (Optional)</label>
            <input
              v-model="newTask.phone_number"
              type="tel"
              placeholder="Phone number for appointments..."
              class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <!-- Schedule time -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Schedule Reminder (Optional)</label>
            <input
              v-model="newTask.schedule_time"
              type="datetime-local"
              :min="minDateTime"
              class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p class="text-xs text-gray-500 mt-1">Limited to 1 reminder per day</p>
          </div>

          <!-- Notification type -->
          <div v-if="newTask.schedule_time">
            <label class="block text-sm font-medium text-gray-700 mb-1">Notification Type</label>
            <select
              v-model="newTask.notification_type"
              class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="email">Email</option>
              <option value="push">Push Notification</option>
            </select>
          </div>

          <!-- Website links -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Website Links (Optional)</label>
            <div class="space-y-2">
              <div v-for="(_, index) in newTask.website_links" :key="index" class="flex space-x-2">
                <input
                  v-model="newTask.website_links[index]"
                  type="url"
                  placeholder="https://example.com"
                  class="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  @click="removeWebsiteLink(index)"
                  class="px-3 py-2 text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              </div>
              <button
                @click="addWebsiteLink"
                class="text-sm text-purple-600 hover:text-purple-800"
              >
                + Add Website Link
              </button>
            </div>
          </div>


        </div>

        <!-- Action buttons -->
        <div class="flex space-x-2 pt-2">
          <button
            @click="addTask"
            :disabled="!canAddTask"
            class="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            :title="!canAddTask ? buttonDisabledReason : 'Add new task'"
          >
            <span v-if="loading">Adding...</span>
            <span v-else>Add Task</span>
          </button>
          <button
            v-if="showAdvancedFields"
            @click="resetForm"
            class="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium"
          >
            Clear
          </button>
        </div>

        <!-- Debug info (remove in production) -->
        <div v-if="false" class="text-xs text-gray-500 mt-2">
          Debug: task="{{newTask.task}}", loading={{loading}}, canAdd={{canAddTask}}
        </div>
      </div>
    </div>

    <!-- Error message -->
    <div v-if="error" class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
      {{ error }}
    </div>

    <!-- Task list -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="loading && tasks.length === 0" class="text-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
        <p class="text-gray-500 mt-2">Loading tasks...</p>
      </div>

      <div v-else-if="tasks.length === 0" class="text-center py-8">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 4h.01M9 16h.01"></path>
        </svg>
        <p class="text-gray-500 mb-2">No tasks yet</p>
        <p class="text-gray-400 text-sm">Add your first task above to get started</p>
      </div>

      <div v-else class="space-y-2">
        <div
          v-for="task in tasks"
          :key="task.id"
          class="p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
        >
          <!-- Task header -->
          <div class="flex items-start justify-between mb-2">
            <div class="flex-1">
              <p class="font-medium text-gray-900">{{ task.task }}</p>
              <div class="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                <span>{{ formatDate(task.created_at) }}</span>
                <span v-if="task.schedule_time" class="text-purple-600">
                  📅 {{ formatDate(task.schedule_time) }}
                </span>
                <span v-if="task.phone_number" class="text-blue-600">
                  📞 {{ task.phone_number }}
                </span>
              </div>
            </div>
            <div class="flex space-x-1">
              <button
                @click="editTask(task)"
                class="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                title="Edit task"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              </button>
              <button
                @click="deleteTaskConfirm(task)"
                class="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Delete task"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Website links -->
          <div v-if="task.website_links && task.website_links.length > 0" class="mb-2">
            <div class="flex flex-wrap gap-2">
              <a
                v-for="(link, index) in task.website_links"
                :key="index"
                :href="link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full hover:bg-blue-200 transition-colors"
              >
                🔗 {{ getDomainFromUrl(link) }}
              </a>
            </div>
          </div>


        </div>
      </div>
    </div>

    <!-- Load more button -->
    <div v-if="hasMore" class="mt-4 text-center">
      <button
        @click="loadMoreTasks"
        :disabled="loading"
        class="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
      >
        <span v-if="loading">Loading...</span>
        <span v-else>Load More</span>
      </button>
    </div>
  </div>
</template>

<script>
import { createTask, getTasks, deleteTask, validateTaskData } from '@/services/taskService'
import { isAuthenticated } from '@/services/authService'

export default {
  name: 'TaskManagementEnhanced',
  data() {
    return {
      tasks: [],
      newTask: {
        task: '',
        phone_number: '',
        website_links: [],
        schedule_time: '',
        notification_type: 'email'
      },
      showAdvancedFields: false,
      loading: false,

      error: null,
      hasMore: true,
      currentOffset: 0,
      limit: 20
    }
  },
  computed: {
    minDateTime() {
      const now = new Date()
      now.setMinutes(now.getMinutes() + 5)
      return now.toISOString().slice(0, 16)
    },

    canAddTask() {
      // Check if task text is provided and not loading
      const hasTaskText = this.newTask.task && this.newTask.task.trim().length > 0;
      const notLoading = !this.loading;

      return hasTaskText && notLoading;
    },

    buttonDisabledReason() {
      if (this.loading) {
        return 'Adding task...';
      }
      if (!this.newTask.task || !this.newTask.task.trim()) {
        return 'Please enter a task description';
      }
      return '';
    }
  },
  async mounted() {
    if (isAuthenticated()) {
      await this.loadTasks()
    }
  },
  emits: ['task-created', 'task-deleted'],
  methods: {
    async loadTasks(reset = true) {
      if (!isAuthenticated()) {
        this.error = 'Please log in to view your tasks'
        return
      }

      try {
        this.loading = true
        this.error = null

        // Check cache first for initial load
        if (reset) {
          const cachedData = this.getTasksFromCache();
          if (cachedData) {
            this.tasks = cachedData.tasks;
            this.currentOffset = cachedData.tasks.length;
            this.hasMore = cachedData.tasks.length === this.limit;
            this.loading = false;
            return;
          }
        }

        const offset = reset ? 0 : this.currentOffset
        const response = await getTasks(this.limit, offset)

        if (response.success) {
          if (reset) {
            this.tasks = response.tasks
            this.currentOffset = response.tasks.length

            // Cache the data
            this.cacheTasksData({
              tasks: response.tasks,
              total: response.tasks.length,
              timestamp: Date.now()
            });
          } else {
            this.tasks.push(...response.tasks)
            this.currentOffset += response.tasks.length
          }

          this.hasMore = response.tasks.length === this.limit
        } else {
          this.error = response.message || 'Failed to load tasks'
        }
      } catch (error) {
        console.error('Error loading tasks:', error)
        this.error = 'Failed to load tasks. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async loadMoreTasks() {
      await this.loadTasks(false)
    },

    async addTask() {
      // Prevent multiple submissions
      if (this.loading) {
        console.log('Task creation already in progress');
        return;
      }

      if (!this.newTask.task.trim()) {
        this.error = 'Please enter a task description';
        return;
      }

      const validation = validateTaskData(this.newTask);
      if (!validation.isValid) {
        this.error = validation.errors.join(', ');
        return;
      }

      try {
        this.loading = true;
        this.error = null;

        console.log('Creating task:', this.newTask.task.trim());

        const taskData = {
          ...this.newTask,
          task: this.newTask.task.trim(),
          website_links: this.newTask.website_links.filter(link => link.trim()),
          image_links: [] // Keep empty for database compatibility
        };

        const response = await createTask(taskData);

        if (response.success) {
          console.log('Task created successfully:', response.task);
          this.tasks.unshift(response.task);
          this.resetForm();
          // Clear cache and emit event
          this.clearTasksCache();
          this.$emit('task-created');
        } else {
          console.error('Task creation failed:', response.message);
          this.error = response.message || 'Failed to create task';
        }
      } catch (error) {
        console.error('Error creating task:', error);
        if (error.response?.status === 429) {
          this.error = 'Rate limit exceeded. You can only create 1 reminder per day.';
        } else if (error.response?.status === 401) {
          this.error = 'Please log in to create tasks.';
        } else {
          this.error = 'Failed to create task. Please try again.';
        }
      } finally {
        // Ensure loading state is always reset
        this.loading = false;
        console.log('Task creation process completed, loading set to false');
      }
    },

    async editTask(taskToEdit) {
      // For now, just show an alert. In a full implementation, you'd open an edit modal
      alert(`Edit functionality would open a modal with the task details for editing: "${taskToEdit.task}"`)
    },

    // Helper function to convert stored datetime back to datetime-local format
    formatDateTimeForInput(dateString) {
      if (!dateString) return '';

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return '';
        }

        // Extract UTC components and format for datetime-local input
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const hour = String(date.getUTCHours()).padStart(2, '0');
        const minute = String(date.getUTCMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hour}:${minute}`;
      } catch (error) {
        console.error('Error formatting datetime for input:', error);
        return '';
      }
    },

    async deleteTaskConfirm(task) {
      if (!confirm(`Are you sure you want to delete this task: "${task.task}"?`)) {
        return
      }

      try {
        this.loading = true
        this.error = null

        const response = await deleteTask(task.id)

        if (response.success) {
          const index = this.tasks.findIndex(t => t.id === task.id)
          if (index !== -1) {
            this.tasks.splice(index, 1)
          }
          // Clear cache and emit event
          this.clearTasksCache()
          this.$emit('task-deleted')
        } else {
          this.error = response.message || 'Failed to delete task'
        }
      } catch (error) {
        console.error('Error deleting task:', error)
        this.error = 'Failed to delete task. Please try again.'
      } finally {
        this.loading = false
      }
    },



    addWebsiteLink() {
      if (this.newTask.website_links.length < 10) {
        this.newTask.website_links.push('')
      }
    },

    removeWebsiteLink(index) {
      this.newTask.website_links.splice(index, 1)
    },



    resetForm() {
      this.newTask = {
        task: '',
        phone_number: '',
        website_links: [],
        schedule_time: '',
        notification_type: 'email'
      }
      this.showAdvancedFields = false
      this.error = null
    },

    formatDate(dateString) {
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return 'Invalid Date';
        }

        // For scheduled times, we want to display the exact time the user selected
        // without timezone conversion (like Todoist, Asana, etc.)
        // We treat the stored UTC time as "naive" datetime

        // Extract the date/time components from the UTC date
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth();
        const day = date.getUTCDate();
        const hour = date.getUTCHours();
        const minute = date.getUTCMinutes();

        // Create a new date object in local timezone with the same components
        const localDate = new Date(year, month, day, hour, minute);

        const dateOptions = {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        };
        const timeOptions = {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        };

        const formattedDate = localDate.toLocaleDateString(undefined, dateOptions);
        const formattedTime = localDate.toLocaleTimeString(undefined, timeOptions);

        return `${formattedDate} ${formattedTime}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Invalid Date';
      }
    },

    getDomainFromUrl(url) {
      try {
        return new URL(url).hostname.replace('www.', '')
      } catch {
        return url
      }
    },

    // Task cache management methods
    getTasksFromCache() {
      try {
        const cached = localStorage.getItem('tasks_cache');
        if (!cached) return null;

        const data = JSON.parse(cached);
        const now = Date.now();
        const cacheAge = now - data.timestamp;
        const cacheExpiry = 30 * 60 * 1000; // 30 minutes

        if (cacheAge > cacheExpiry) {
          localStorage.removeItem('tasks_cache');
          return null;
        }

        return data;
      } catch (error) {
        console.error('Error reading tasks cache:', error);
        localStorage.removeItem('tasks_cache');
        return null;
      }
    },

    cacheTasksData(data) {
      try {
        localStorage.setItem('tasks_cache', JSON.stringify(data));
      } catch (error) {
        console.error('Error caching tasks data:', error);
      }
    },

    clearTasksCache() {
      localStorage.removeItem('tasks_cache');
    }

  }
}
</script>

<style scoped>
/* Smooth transitions */
* {
  transition: all 0.2s ease;
}

/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Hover effects */
.hover\:shadow-md:hover {
  transform: translateY(-1px);
}

/* Loading animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Rotate animation for chevron */
.rotate-90 {
  transform: rotate(90deg);
}

/* Focus styles */
input:focus,
select:focus,
textarea:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}

/* Button hover effects */
button:hover:not(:disabled) {
  transform: translateY(-1px);
}

button:active:not(:disabled) {
  transform: translateY(0);
}




</style>
